import {
  lazy,
  type ComponentType,
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useTransition,
} from 'react'

import { ClientI18NProvider, useI18N } from '@susu/i18n/client'
import { CustomMadeDetailsModal, OrderListContainer } from '@susu/order-list'
import { PreferenceCenterCore } from '@susu/preference-center'
import { Button, Icon, ListItem } from '@susu/ui/components'
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { fetchCustomerData, updateCustomer } from '../api/customerApi'
import avo from '../avo/avo'
import {
  getNewArrivalsUrl,
  getReturnsUrl,
  getSizePassportJournalUrl,
  getStoresUrl,
} from '../config/api.config'
import { BootSkeletonProvider } from '../contexts/BootSkeletonContext'
import { CustomerDataProvider } from '../contexts/CustomerDataContext'
import {
  CustomerHubProvider,
  ROLE,
  type Role,
  type TrackingContext,
  useCustomerHub,
} from '../contexts/CustomerHubContext'
import { ModalProvider, useModal } from '../contexts/ModalContext'
import { ToastProvider, useToast } from '../contexts/ToastContext'
import { useActiveOrders } from '../hooks/useActiveOrders'
import { useBootSkeletonSkip } from '../hooks/useBootSkeletonSkip'
import { useNbaCard } from '../hooks/useNbaCard'
import { useReturnLabels } from '../hooks/useReturnLabels'
import { useSkeletonPhase } from '../hooks/useSkeletonPhase'
import { useWarmOrderHistory } from '../hooks/useWarmOrderHistory'
import {
  type CustomerHubAnalyticsConfig,
  initializeAnalytics,
  setProfileSystemProperties,
  trackActiveCardShowReturnLabelClicked,
} from '../lib/analytics'
import { getEmployeeIdFromUrl } from '../lib/employeeId'
import {
  isActiveCardsEnabled,
  isOverviewTabEnabled,
  isCancellationFlowEnabled,
  isNbaCardsEnabled,
  isSizePassportEnabled,
  isWardrobeEnabled,
} from '../lib/growthbook'
import { isCustomerTimelineHidden } from '../lib/timelineVisibility'
import {
  getResolvedCountryCode,
  normalizeCountryCode,
} from '../lib/urlCountryCode'
import { cn } from '../lib/utils'
import { translations } from '../locales'
import { WardrobeItemCustomMade } from '../types/wardrobe'
import { toOrderStateCards, toTimelineLabels } from '../utils/activeOrderCards'
import { getTokenFromUrl } from '../utils/auth'
import { copyToClipboard } from '../utils/clipboard'
import { configureDebugLogging, debug } from '../utils/debug'
import { normalizeEnvironment } from '../utils/normalizeEnv'
import { getPassportClientForRole } from '../utils/sizePassport'

import ActiveOrders from './ActiveOrders'
import AddressBookModal from './AddressBookModal'
import AnimatedContentWrapper from './AnimatedContentWrapper'
import BootContentSkeleton, {
  bootSkeletonCoversTab,
} from './BootContentSkeleton'
import CustomerInfoModal from './CustomerInfoModal'
import CustomerTimeline from './CustomerTimeline'
import { EmployeeBadge } from './EmployeeBadge'
import EmptyState from './EmptyState'
import HubShellSkeleton from './HubShellSkeleton'
import ItemsInCart from './ItemsInCart'
import NbaCard, { type NbaFeedback } from './NbaCard'
import RecentlyBrowsed from './RecentlyBrowsed'
import { getSizePassportEmptyStateProps } from './sizePassportEmptyState'
import WardrobeTab from './Wardrobe/WardrobeTab'

import type { ActiveOrder } from '../api/activeOrdersApi'
import type { Environment, EnvironmentWithChina } from '../App'
import type {
  CountryValueType,
  LanguageValueType,
  TabValueType,
} from '../avo/avo'
import type { CustomerData, UpdateCustomerPayload } from '../types/customer'
import type { OrderStateCard, OrderStateCta } from '@susu/order-list'

// Create QueryClient instance for CustomerHubCore
const customerHubQueryClient = new QueryClient()

// Dynamic import for framer-motion to reduce initial bundle size
const MotionDiv = lazy(() =>
  import('framer-motion').then((mod) => ({ default: mod.motion.div })),
)

export type { Role }
export type CustomerStatus = 'activeInCart' | 'waiting' | 'serving' | ''

type TabId = 'overview' | 'orders' | 'sizePassport' | 'wardrobe' | 'settings'

const TAB_TO_AVO: Record<TabId, TabValueType> = {
  overview: avo.Tab.OVERVIEW,
  orders: avo.Tab.ORDERS,
  sizePassport: avo.Tab.SIZE_PASSPORT,
  // TODO(RP-6127): swap for `avo.Tab.WARDROBE` once Avo branch
  // `da-1364wardrobe-component` (which adds the value) is merged and `avo pull`
  // regenerates `src/avo/avo.ts`. The generated tracker forwards the list as-is.
  wardrobe: 'Wardrobe' as TabValueType,
  settings: avo.Tab.SETTINGS,
}

const TABS = [
  { id: 'overview' as const, disabled: false },
  { id: 'orders' as const, disabled: false },
  { id: 'settings' as const, disabled: false },
] as const

/** Maps tab IDs to their stable data-testid suffixes (must never change). */
const TAB_TEST_IDS: Record<TabId, string> = {
  overview: 'overview',
  orders: 'orders',
  sizePassport: 'size-passport',
  wardrobe: 'wardrobe',
  settings: 'settings',
}

type TabItem = {
  id: TabId
  disabled: boolean
}

function getDefaultTab(role?: Role, isOverviewTabOn?: boolean): TabId {
  if (role !== ROLE.CUSTOMER) return 'overview'
  return isOverviewTabOn ? 'overview' : 'orders'
}

/**
 * Customers only get the Overview tab when `CH-overview-tab` is on for them.
 * Style advisors always get it, so the flag never applies to them.
 */
function getRoleTabs(
  tabs: readonly TabItem[],
  role?: Role,
  isOverviewTabOn?: boolean,
) {
  return role === ROLE.CUSTOMER && !isOverviewTabOn
    ? tabs.filter((tab) => tab.id !== 'overview')
    : tabs
}

/**
 * The tab list for the given flag state, in render order. Shared by the loaded
 * nav and the boot skeleton so the placeholder can never disagree with the nav
 * it is standing in for.
 */
function buildTabs({
  isSizePassportTabEnabled,
  isWardrobeTabEnabled,
}: {
  isSizePassportTabEnabled: boolean
  isWardrobeTabEnabled: boolean
}): TabItem[] {
  let tabs: TabItem[] = [...TABS]

  if (isSizePassportTabEnabled) {
    tabs = [
      tabs[0],
      tabs[1],
      { id: 'sizePassport' as const, disabled: false },
      tabs[2],
    ]
  }

  if (isWardrobeTabEnabled) {
    const settingsIndex = tabs.findIndex((tab) => tab.id === 'settings')
    tabs = [
      ...tabs.slice(0, settingsIndex),
      { id: 'wardrobe' as const, disabled: false },
      ...tabs.slice(settingsIndex),
    ]
  }

  return tabs
}

function getAllowedActiveTab({
  tab,
  role,
  isSizePassportTabEnabled,
  isWardrobeTabEnabled,
  isOverviewTabOn,
}: {
  tab: TabId
  role?: Role
  isSizePassportTabEnabled: boolean
  isWardrobeTabEnabled: boolean
  isOverviewTabOn: boolean
}): TabId {
  if (tab === 'overview' && role === ROLE.CUSTOMER && !isOverviewTabOn) {
    return 'orders'
  }
  if (tab === 'sizePassport' && !isSizePassportTabEnabled) {
    return getDefaultTab(role, isOverviewTabOn)
  }
  if (tab === 'wardrobe' && !isWardrobeTabEnabled) {
    return getDefaultTab(role, isOverviewTabOn)
  }

  return tab
}

function getSizePassportEnv(env: Environment): Environment {
  return env === 'acc' ? 'tst' : env
}

function getTabFromScreenParam(): TabId | undefined {
  if (typeof window === 'undefined') return undefined

  const screenParam = new URLSearchParams(window.location.search).get('screen')
  const normalizedScreen =
    screenParam?.replace(/['"]/g, '').trim().toLowerCase() ?? ''

  if (normalizedScreen === 'orders') return 'orders'
  if (normalizedScreen === 'overview') return 'overview'
  if (
    normalizedScreen === 'sizepassport' ||
    normalizedScreen === 'size-passport' ||
    normalizedScreen === 'size_passport'
  ) {
    return 'sizePassport'
  }
  if (normalizedScreen === 'settings') return 'settings'
  if (normalizedScreen === 'wardrobe') return 'wardrobe'

  return undefined
}

// Animation variants - hoisted to module scope
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 300,
      damping: 25,
    },
  },
}

const tabIndicatorTransition = {
  type: 'spring',
  stiffness: 500,
  damping: 30,
} as const

function StaticContainer({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return <div className={className}>{children}</div>
}

/**
 * Props `CustomerHubCore` hands to the host-provided size passports component.
 * Structurally matches `MeasCustomerProfilesPublicProps` from
 * `@susu/measurements-client`, which depends on this package (via the active
 * customers list), so the component is injected instead of imported.
 */
export interface SizePassportsProps {
  accountNr: string | null | undefined
  client?: 'CustomerRTW' | 'SuitsupplyRTW'
  countryCode: string | null | undefined
  'data-testid'?: string
  env: Environment
  languageCode?: string
  token: string | null | undefined
  onEmptyState?: (isEmpty: boolean) => void
}

interface CustomerHubCoreProps {
  /** Renders the customer's size passports. Without it the tab is not offered. */
  SizePassports?: ComponentType<SizePassportsProps>
  children?: ReactNode
  accountNumber: string
  customerProfileEmail?: string
  env: EnvironmentWithChina
  locationId?: string
  /** Physical store code used for GrowthBook targeting (e.g. timeline hide). */
  locationCode?: string
  customerStatus?: CustomerStatus
  activeTab?: TabId
  onTabChange?: (tab: TabId) => void
  context?: string
  role?: Role
  tracking?: TrackingContext
  authToken?: string
  onLogout?: () => void
  stateContainerClassName?: string
  /** Disable all framer-motion animations (useful when consumed inside Shopify) */
  disableAnimations?: boolean
  analyticsConfig?: CustomerHubAnalyticsConfig
  /** Called when customer profile data has been loaded from customerApi. */
  onCustomerDataLoaded?: (customer: CustomerData) => void
  /** BCP-47 language code for UI text (e.g. 'en', 'nl'). Defaults to the document language. */
  language?: string
}

interface CustomerHubContentProps {
  SizePassports?: ComponentType<SizePassportsProps>
  children?: ReactNode
  customerData: CustomerData
  env: EnvironmentWithChina
  locationId?: string
  locationCode?: string
  customerStatus?: CustomerStatus
  activeTab?: TabId
  onTabChange?: (tab: TabId) => void
  role?: Role
  tracking?: TrackingContext
  authToken?: string
  onLogout?: () => void
  analyticsConfig?: CustomerHubAnalyticsConfig
  /** Entry-fade class from the shell's skeleton phase (RP-5936). */
  shellContentClassName?: string
  /**
   * Flags already resolved during boot. Seeding them here stops the loaded Hub
   * re-resolving from `false` and briefly dropping the sections and tabs the
   * boot screen had already shown (RP-5936).
   */
  bootFlags?: {
    isSizePassportTabEnabled: boolean
    isWardrobeTabEnabled: boolean
    isActiveCardsOn: boolean
    isOverviewTabOn: boolean
  }
  /**
   * The tab whose placeholders the boot screen actually painted, or `null`
   * when it painted none. Sections may only skip the anti-flash delay while
   * this exact tab is still showing (RP-5936).
   */
  bootPaintedTab?: string | null
}

function CustomerHubContent({
  SizePassports,
  children,
  customerData,
  env,
  locationId,
  locationCode,
  customerStatus,
  activeTab: controlledActiveTab,
  onTabChange,
  role,
  tracking,
  authToken,
  onLogout,
  analyticsConfig,
  shellContentClassName,
  bootFlags,
  bootPaintedTab,
}: CustomerHubContentProps) {
  const { t, language } = useI18N()
  const baseEnv = normalizeEnvironment(env)
  // Undefined until the user (or the `screen` param) picks a tab, so the
  // default follows `CH-overview-tab` when it resolves after mount.
  const [internalActiveTab, setInternalActiveTab] = useState<TabId | undefined>(
    getTabFromScreenParam,
  )
  const [isPending, startTransition] = useTransition()
  /** Drives OrderListContainer so the overview can deep-link into an order. */
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)
  const [isCancellationEnabled, setIsCancellationEnabled] = useState(false)
  const [isActiveCardsOn, setIsActiveCardsOn] = useState(
    bootFlags?.isActiveCardsOn ?? false,
  )
  const [isOverviewTabOn, setIsOverviewTabOn] = useState(
    bootFlags?.isOverviewTabOn ?? false,
  )
  const [isNbaCardsOn, setIsNbaCardsOn] = useState(false)
  const [nbaFeedbackAccountNumber, setNbaFeedbackAccountNumber] = useState<
    string | null
  >(null)
  const nbaViewedKeyRef = useRef<string | null>(null)
  const [isSizePassportTabEnabled, setIsSizePassportTabEnabled] = useState(
    bootFlags?.isSizePassportTabEnabled ?? false,
  )
  const [isWardrobeTabEnabled, setIsWardrobeTabEnabled] = useState(
    bootFlags?.isWardrobeTabEnabled ?? false,
  )
  // Shares the overview section's query — same key, so no second fetch.
  const {
    data: fetchedActiveOrders = [],
    isLoading: isActiveOrdersLoading,
    isError: isActiveOrdersError,
  } = useActiveOrders({
    accountNumber: customerData.accountNumber,
    env,
    authToken,
    enabled: isActiveCardsOn,
  })
  // A customer's overview holds nothing but the Active Orders section, so the
  // empty state must yield to it. Still-loading counts as showing it: the
  // section paints its skeleton in that window, and a customer who does have
  // orders must not see the empty state flash in first.
  const hasActiveOrdersSection =
    isActiveCardsOn &&
    !isActiveOrdersError &&
    (isActiveOrdersLoading || fetchedActiveOrders.length > 0)
  const activeOrders = useReturnLabels(fetchedActiveOrders, baseEnv)
  // Host app country code — drives the `locale` on the pick-up appointment deep
  // link, and the size passport journal link further down.
  const resolvedCountryCode = getResolvedCountryCode(customerData.countryCode)
  const orderStateCards = isActiveCardsOn
    ? toOrderStateCards(
        activeOrders,
        t,
        language,
        baseEnv,
        undefined,
        resolvedCountryCode,
        role,
      )
    : []
  const timelineLabels = toTimelineLabels(t)
  const { openModal, closeModal } = useModal()
  const { showToast } = useToast()
  // Copy an item code from the order detail page to the clipboard and confirm
  // via the shared toast, mirroring the item-card copy behaviour. In-store only
  // (the order detail item code is rendered for the style-advisor role).
  const handleCopyProductCode = useCallback(
    async (code: string) => {
      const copied = await copyToClipboard(code.toUpperCase())
      if (copied) {
        showToast(t('toasts.copiedProductCode'))
      }
    },
    [showToast, t],
  )
  const { context, disableAnimations } = useCustomerHub()
  const isAuthRedirect =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('authRedirect') === 'true'
  const reserveFixedFooterInset =
    !isAuthRedirect &&
    (context === 'active-customers-list' || context === 'predict-spring')
  /** ACL / customer-search fixed footer: py-16 + h-44 + py-16 + safe area. */
  const fixedFooterInsetClass = 'h-[calc(5rem+env(safe-area-inset-bottom,0px))]'
  const fixedFooterScrollPadding =
    'pb-[calc(5rem+env(safe-area-inset-bottom,0px))]'
  const resolvedLocationId =
    locationId ??
    (typeof window !== 'undefined'
      ? (new URLSearchParams(window.location.search).get('locationId') ?? '')
      : '')
  const resolvedLocationCode =
    locationCode ??
    (typeof window !== 'undefined'
      ? (new URLSearchParams(window.location.search).get('locationCode') ?? '')
      : '')
  const isTimelineHidden = isCustomerTimelineHidden(resolvedLocationCode)

  const activeTab =
    controlledActiveTab ??
    internalActiveTab ??
    getDefaultTab(role, isOverviewTabOn)

  useEffect(() => {
    let isMounted = true

    void isCancellationFlowEnabled(baseEnv, resolvedLocationId, role)
      .then((enabled) => {
        if (isMounted) setIsCancellationEnabled(enabled)
      })
      .catch(() => {
        if (isMounted) setIsCancellationEnabled(false)
      })

    void isActiveCardsEnabled(baseEnv, resolvedLocationId, role)
      .then((enabled) => {
        if (isMounted) setIsActiveCardsOn(enabled)
      })
      .catch(() => {
        if (isMounted) setIsActiveCardsOn(false)
      })

    void isOverviewTabEnabled(baseEnv, resolvedLocationId, role)
      .then((enabled) => {
        if (isMounted) setIsOverviewTabOn(enabled)
      })
      .catch(() => {
        if (isMounted) setIsOverviewTabOn(false)
      })

    void isNbaCardsEnabled(baseEnv, resolvedLocationId, role)
      .then((enabled) => {
        if (isMounted) setIsNbaCardsOn(enabled)
      })
      .catch(() => {
        if (isMounted) setIsNbaCardsOn(false)
      })

    void isSizePassportEnabled(baseEnv, resolvedLocationId, role)
      .then((enabled) => {
        if (isMounted)
          setIsSizePassportTabEnabled(enabled && Boolean(SizePassports))
      })
      .catch(() => {
        if (isMounted) setIsSizePassportTabEnabled(false)
      })

    void isWardrobeEnabled(baseEnv, resolvedLocationId, role)
      .then((enabled) => {
        if (isMounted) setIsWardrobeTabEnabled(enabled)
      })
      .catch(() => {
        if (isMounted) setIsWardrobeTabEnabled(false)
      })

    return () => {
      isMounted = false
    }
  }, [SizePassports, baseEnv, resolvedLocationId, role])

  const getUserType = () => {
    if (role === ROLE.CUSTOMER) return [avo.UserType.CUSTOMER]
    if (role === ROLE.STYLE_ADVISOR) return [avo.UserType.STYLE_ADVISOR]
    return []
  }

  const getCommonTrackingProps = () => ({
    eventLabel: null,
    eventCategory: null,
  })

  const getNbaTrackingProps = (cardId: string) => ({
    ...getCommonTrackingProps(),
    eventLocation: 'nba_card',
    cardId,
    employeeId: tracking?.employeeId || getEmployeeIdFromUrl(),
    storeId: tracking?.storeId || resolvedLocationId,
    locationCode: resolvedLocationCode,
  })

  const handleNbaFeedback = ({ cardId, rating, notes }: NbaFeedback) => {
    setNbaFeedbackAccountNumber(customerData.accountNumber)
    avo.nbaFeedbackSubmitted({
      ...getNbaTrackingProps(cardId),
      rating,
      feedback: notes,
    })
  }

  // Get display name based on role
  const getDisplayName = () => {
    if (!customerData) return ''
    const sanitizeNamePart = (value?: string | null) => {
      const normalized = value?.trim()
      if (!normalized || normalized === '[Not provided]') return ''
      return normalized
    }

    if (role === ROLE.CUSTOMER) {
      const firstName = sanitizeNamePart(customerData.firstName)
      if (!firstName) {
        return t('greeting.hello')
      }
      return t('greeting.helloName', { name: firstName })
    }

    return `${customerData.firstName} ${customerData.lastName}`.trim()
  }

  const baseTabs = buildTabs({
    isSizePassportTabEnabled,
    isWardrobeTabEnabled,
  })
  const availableTabs = getRoleTabs(baseTabs, role, isOverviewTabOn)
  const effectiveActiveTab = getAllowedActiveTab({
    tab: activeTab,
    role,
    isSizePassportTabEnabled,
    isWardrobeTabEnabled,
    isOverviewTabOn,
  })

  useWarmOrderHistory({
    activeTab: effectiveActiveTab,
    accountNumber: customerData.accountNumber,
    env: baseEnv,
    authToken,
  })

  const skipSkeletonDelay = useBootSkeletonSkip(
    bootPaintedTab,
    effectiveActiveTab,
  )

  // Same query key as NbaCard, so this reads its cache rather than fetching.
  const isNbaCardOnScreen =
    role === ROLE.STYLE_ADVISOR &&
    isNbaCardsOn &&
    effectiveActiveTab === 'overview'
  const { data: nbaCard } = useNbaCard({
    accountNumber: customerData.accountNumber,
    env,
    authToken,
    enabled: isNbaCardOnScreen,
  })
  const viewedNbaCardId = isNbaCardOnScreen ? nbaCard?.cardId : undefined

  // Hand-rolled useEffectEvent: the repo pins @types/react to 18.3.1, which
  // does not declare it. Keeps the effect below keyed on the card alone while
  // still reading the latest tracking props.
  const trackNbaCardViewedRef = useRef<(cardId: string) => void>(() => {})
  useLayoutEffect(() => {
    trackNbaCardViewedRef.current = (cardId: string) => {
      avo.nbaCardViewed(getNbaTrackingProps(cardId))
    }
  })

  // One nba_card_viewed per profile visit. The overview remounts the card on
  // every tab switch, which would otherwise inflate the feedback-rate
  // denominator.
  useEffect(() => {
    if (viewedNbaCardId === undefined) return
    const key = `${customerData.accountNumber}:${viewedNbaCardId}`
    if (nbaViewedKeyRef.current === key) return
    nbaViewedKeyRef.current = key
    trackNbaCardViewedRef.current(viewedNbaCardId)
  }, [viewedNbaCardId, customerData.accountNumber])

  // Keep the active tab scrolled into full view on the horizontally scrollable
  // mobile tab bar so its label is never clipped at the screen edge (RP-5808).
  const tabButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  useEffect(() => {
    const activeButton = tabButtonRefs.current[effectiveActiveTab]
    activeButton?.scrollIntoView({
      inline: 'center',
      block: 'nearest',
      behavior: disableAnimations ? 'auto' : 'smooth',
    })
  }, [effectiveActiveTab, disableAnimations])

  const goToTab = (tab: TabId) => {
    startTransition(() => {
      if (onTabChange) {
        onTabChange(tab)
      } else {
        setInternalActiveTab(tab)
      }
    })
  }

  const handleTabClick = (tab: TabId) => {
    if (tab === 'sizePassport' && !isSizePassportTabEnabled) return
    if (tab === 'wardrobe' && !isWardrobeTabEnabled) return

    // Reaching Orders from the nav always lands on the list, never on whichever
    // order was open last.
    if (tab === 'orders') setSelectedOrderId(null)

    avo.tabClicked({
      ...getCommonTrackingProps(),
      eventLocation: 'customer_hub',
      tab: [TAB_TO_AVO[tab]],
      userType: getUserType(),
    })

    goToTab(tab)
  }

  // Modal handlers
  const handleCustomerInfo = () => {
    avo.settingsCustomerInfoClicked({
      ...getCommonTrackingProps(),
      eventLocation: 'customer_hub_settings',
      userType: getUserType(),
    })
    if (customerData) {
      openModal(
        <CustomerInfoModal customerData={customerData} />,
        t('modals.personalInfo'),
      )
    }
  }

  const handleAddressBook = () => {
    avo.settingsAddressBookClicked({
      ...getCommonTrackingProps(),
      eventLocation: 'customer_hub_settings',
      userType: getUserType(),
    })
    openModal(
      <AddressBookModal customerData={customerData} tracking={tracking} />,
      t('modals.addressBook'),
    )
  }

  const handleCommunication = () => {
    avo.settingsCommunicationClickled({
      ...getCommonTrackingProps(),
      eventLocation: 'customer_hub_settings',
      userType: getUserType(),
    })
    const token = authToken || getTokenFromUrl()
    openModal(
      <PreferenceCenterCore
        env={baseEnv}
        executedBy="customer-hub"
        email={customerData.email}
        token={token || undefined}
        isStandalone={false}
        // Embedded, so it cannot assume the hub's own locale. Hand it the one
        // we resolved instead of letting it guess from the storefront URL.
        language={language}
        analyticsConfig={analyticsConfig}
        onSave={({ newsletterStatus }) => {
          avo.communicationPreferencesSaved({
            ...getCommonTrackingProps(),
            eventLocation: 'customer_hub_settings',
            communicationPreferences: { newsletterStatus },
            userType: getUserType(),
          })
        }}
        onSaveSuccess={closeModal}
      />,
      t('modals.communication'),
    )
  }

  const handleShopNewArrivals = () => {
    avo.newArrivalsButtonClicked({
      ...getCommonTrackingProps(),
      eventLocation: 'customer_hub_overview',
      userType: getUserType(),
    })
    window.open(
      getNewArrivalsUrl(baseEnv, resolvedCountryCode),
      '_blank',
      'noopener,noreferrer',
    )
  }

  // Active order cards open that order's details, which live under the Orders
  // tab — so select the order first, then switch tabs.
  const handleActiveOrderClick = (order: ActiveOrder) => {
    avo.orderClicked({
      ...getCommonTrackingProps(),
      eventLocation: 'customer_hub_active_card',
      orderId: order.order_id,
      userType: getUserType(),
    })
    setSelectedOrderId(order.order_id)
    goToTab('orders')
  }

  /** Full card action row (RP-5244/5246/5249). */
  const handleOrderStateCtaClick = (
    card: OrderStateCard,
    cta: OrderStateCta,
  ) => {
    const common = {
      ...getCommonTrackingProps(),
      orderId: card.orderId,
      userType: getUserType(),
    }

    switch (cta.kind) {
      case 'trackOrder':
        avo.trackYourOrderClicked(common)
        break
      case 'returnOrExchange':
        avo.returnOrderClicked({
          ...common,
          eventLocation: 'order_details_active_card',
          // The plan added currency; an order card carries none here.
          currency: null,
        })
        break
      case 'bookPickupAppointment':
        avo.bookPickupAppointmentClicked({
          ...common,
          // The plan only distinguishes Order from Alteration; custom made
          // rides along as an order card.
          cardType:
            card.timelineVariant === 'alteration'
              ? avo.CardType.ALTERATION
              : avo.CardType.ORDER,
        })
        break
      case 'showQrCode':
        trackActiveCardShowReturnLabelClicked(common)
        break
    }
  }

  // The event's `category` property carries the governed wardrobe section
  // (RP-5934). The item's own `category` used to hold the department, which is
  // what this reported; since the trait re-pointed it, `section` is both the
  // closer continuation of that vocabulary and the only one of the two that is
  // populated on Custom Made items.
  const handleWardrobeItemClicked = (
    section: string,
    itemCode: string,
    customMade?: WardrobeItemCustomMade,
  ) => {
    avo.wardrobeItemClicked({
      ...getCommonTrackingProps(),
      userType: getUserType(),
      category: section,
      productId: itemCode,
      eventLocation: 'customer_hub_wardrobe',
      quantity: 1,
      isCustomMade: Boolean(customMade),
    })
  }

  const sizePassportToken = authToken || getTokenFromUrl()

  const handleBookAppointment = () => {
    window.open(
      getStoresUrl(baseEnv, resolvedCountryCode),
      '_blank',
      'noopener,noreferrer',
    )
  }

  const handleSizePassportLearnMore = () => {
    window.open(
      getSizePassportJournalUrl(baseEnv, resolvedCountryCode),
      '_blank',
      'noopener,noreferrer',
    )
  }

  // Track empty state for orders tab
  const [hasOrders, setHasOrders] = useState(true)

  // Track empty state for size passport tab
  const [hasPassports, setHasPassports] = useState(true)

  // Stable callbacks: child components consume these inside effect dependency
  // arrays, so an inline arrow would re-run those effects every render.
  const handleOrdersEmptyState = useCallback(
    (isEmpty: boolean) => setHasOrders(!isEmpty),
    [],
  )
  const handlePassportsEmptyState = useCallback(
    (isEmpty: boolean) => setHasPassports(!isEmpty),
    [],
  )

  // Empty state props based on role
  const getOverviewEmptyStateProps = () => ({
    testId: 'overview-empty-state',
    title: t('overview.emptyTitle'),
    primaryAction:
      role === ROLE.CUSTOMER
        ? {
            label: t('actions.shopNewArrivals'),
            onClick: handleShopNewArrivals,
            testId: 'overview-shop-new-arrivals-button',
          }
        : undefined,
  })

  const getOrdersEmptyStateProps = () => ({
    testId: 'orders-empty-state',
    title:
      role === ROLE.CUSTOMER
        ? t('orders.emptyCustomer')
        : t('orders.emptyStyleAdvisor'),
    primaryAction:
      role === ROLE.CUSTOMER
        ? {
            label: t('actions.shopNewArrivals'),
            onClick: handleShopNewArrivals,
            testId: 'orders-shop-new-arrivals-button',
          }
        : undefined,
  })

  const sizePassportEmptyStateProps = getSizePassportEmptyStateProps({
    role,
    onBookAppointment: handleBookAppointment,
    onLearnMore: handleSizePassportLearnMore,
    t,
  })

  // Wrapper that renders a MotionDiv or plain div depending on the animation flag
  const Container = disableAnimations ? StaticContainer : MotionDiv
  const containerProps = disableAnimations
    ? {}
    : { variants: containerVariants, initial: 'hidden', animate: 'visible' }
  const itemProps = disableAnimations ? {} : { variants: itemVariants }

  // Default tab content rendering
  // The tab content suspends while the animation chunk loads. Falling back to
  // the same placeholders the boot screen painted keeps the column filled
  // across that gap — a blank fallback here undoes the whole point of showing
  // the shell and the section placeholders together (RP-5936).
  const bootContentFallback = (
    <BootContentSkeleton
      tab={effectiveActiveTab}
      showActiveOrders={isActiveCardsOn}
      showTimeline={role === ROLE.STYLE_ADVISOR && !isTimelineHidden}
    />
  )

  const renderDefaultTabContent = () => {
    switch (effectiveActiveTab) {
      case 'overview':
        return (
          <Suspense fallback={bootContentFallback}>
            <Container
              className="flex flex-col gap-48 pt-24 pb-120 md:max-lg:mx-auto md:max-w-[522px] 3xl:max-w-[574px]"
              {...containerProps}
            >
              {role === ROLE.STYLE_ADVISOR && isNbaCardsOn && (
                <Container className="empty:hidden" {...itemProps}>
                  <NbaCard
                    accountNumber={customerData.accountNumber}
                    authToken={authToken}
                    env={env}
                    isEnabled={isNbaCardsOn}
                    hasSentFeedback={
                      nbaFeedbackAccountNumber === customerData.accountNumber
                    }
                    onFeedback={handleNbaFeedback}
                  />
                </Container>
              )}
              {isActiveCardsOn && (
                <Container className="empty:hidden" {...itemProps}>
                  <ActiveOrders
                    accountNumber={customerData.accountNumber}
                    authToken={authToken}
                    env={env}
                    isEnabled={isActiveCardsOn}
                    onOrderClick={handleActiveOrderClick}
                  />
                </Container>
              )}
              {role === ROLE.STYLE_ADVISOR && !isTimelineHidden && (
                <Container {...itemProps}>
                  <CustomerTimeline
                    token={authToken || ''}
                    customerEmail={customerData.email}
                    accountNumber={customerData.accountNumber}
                    locationId={resolvedLocationId}
                    env={baseEnv}
                    customerStatus={customerStatus}
                  />
                </Container>
              )}
              <Container
                className="flex flex-col gap-48 empty:hidden"
                {...itemProps}
              >
                <ItemsInCart
                  accountNumber={customerData.accountNumber}
                  authToken={authToken}
                  env={env}
                  locationId={resolvedLocationId}
                  onLoaded={() => {
                    avo.itemsInCartLoaded({
                      ...getCommonTrackingProps(),
                      eventLocation: 'customer_hub',
                      userType: getUserType(),
                    })
                  }}
                  role={role}
                />

                <RecentlyBrowsed
                  accountNumber={customerData.accountNumber}
                  authToken={authToken}
                  env={env}
                  locationId={resolvedLocationId}
                  role={role}
                />

                {role === ROLE.CUSTOMER && !hasActiveOrdersSection && (
                  <EmptyState {...getOverviewEmptyStateProps()} />
                )}
              </Container>
            </Container>
          </Suspense>
        )
      case 'orders':
        return (
          <Suspense fallback={bootContentFallback}>
            <Container className="w-full" {...containerProps}>
              <Container {...itemProps}>
                {!hasOrders ? (
                  <EmptyState {...getOrdersEmptyStateProps()} />
                ) : (
                  <OrderListContainer
                    authToken={authToken || ''}
                    accountNumber={customerData.accountNumber}
                    env={baseEnv}
                    orderStateCards={orderStateCards}
                    onOrderStateClick={handleOrderStateCtaClick}
                    timelineLabels={timelineLabels}
                    t={t}
                    language={language}
                    selectedOrderId={selectedOrderId}
                    onSelectedOrderIdChange={setSelectedOrderId}
                    onEmptyState={handleOrdersEmptyState}
                    skipSkeletonDelay={skipSkeletonDelay}
                    role={role}
                    isCancellationFlowEnabled={isCancellationEnabled}
                    onOrderClicked={(orderId) => {
                      avo.orderClicked({
                        ...getCommonTrackingProps(),
                        eventLocation: 'order_list',
                        orderId,
                        userType: getUserType(),
                      })
                    }}
                    onReturnClicked={(orderId) => {
                      avo.returnOrderClicked({
                        ...getCommonTrackingProps(),
                        eventLocation: 'order_details',
                        orderId,
                        userType: getUserType(),
                        currency: null,
                      })
                      window.location.assign(
                        getReturnsUrl(baseEnv, orderId, resolvedCountryCode),
                      )
                    }}
                    onCancellationClicked={() => {
                      avo.cancellationClicked({
                        ...getCommonTrackingProps(),
                        eventLocation: 'order_details',
                        employeeId: tracking?.employeeId ?? '',
                        userType: getUserType(),
                      })
                    }}
                    onCancellationConfirmed={() => {
                      avo.cancellationConfirmed({
                        ...getCommonTrackingProps(),
                        eventLocation: 'order_details',
                        employeeId: tracking?.employeeId ?? '',
                        userType: getUserType(),
                      })
                    }}
                    onFaqOpen={(title) => {
                      avo.faqOpen({
                        ...getCommonTrackingProps(),
                        eventLabel: title,
                        eventLocation: 'order_details',
                        userType: getUserType(),
                      })
                    }}
                    onItemDetailsClick={(item) => {
                      if (!item.customMadeDetails) return
                      // Reorder CTA commented out until reorder fix lands (flagged ticket).
                      openModal(
                        <CustomMadeDetailsModal
                          customMadeDetails={item.customMadeDetails}
                          name={item.name}
                        />,
                        t('modals.customMadeDetails'),
                      )
                    }}
                    onCopyProductCode={
                      role === ROLE.STYLE_ADVISOR
                        ? handleCopyProductCode
                        : undefined
                    }
                  />
                )}
              </Container>
            </Container>
          </Suspense>
        )
      case 'sizePassport':
        return (
          <Suspense fallback={<div className="flex flex-col" />}>
            <Container
              className="w-full flex flex-col max-md:px-16 max-lg:py-24 md:max-w-[490px] md:max-lg:mx-auto xl:max-w-[524px] 3xl:max-w-[574px]"
              {...containerProps}
            >
              <Container {...itemProps}>
                {!hasPassports || !SizePassports ? (
                  <EmptyState {...sizePassportEmptyStateProps} />
                ) : (
                  <SizePassports
                    data-testid="customer-hub-size-passport"
                    client={getPassportClientForRole(role)}
                    env={getSizePassportEnv(baseEnv)}
                    token={
                      sizePassportToken
                        ? `Bearer ${sizePassportToken}`
                        : sizePassportToken
                    }
                    countryCode={resolvedCountryCode}
                    accountNr={customerData.accountNumber}
                    languageCode={language}
                    onEmptyState={handlePassportsEmptyState}
                  />
                )}
              </Container>
            </Container>
          </Suspense>
        )
      case 'wardrobe':
        return (
          <WardrobeTab
            accountNumber={customerData.accountNumber}
            env={env}
            authToken={authToken}
            role={role}
            countryCode={resolvedCountryCode}
            onShopNewArrivals={handleShopNewArrivals}
            onItemClicked={handleWardrobeItemClicked}
          />
        )
      case 'settings':
        return (
          <Suspense fallback={<div className="flex flex-col" />}>
            <Container
              className="w-full flex flex-col max-md:px-16 max-lg:py-24 md:max-w-[490px] md:max-lg:mx-auto xl:max-w-[524px] 3xl:max-w-[574px]"
              {...containerProps}
            >
              <Container {...itemProps}>
                <ListItem
                  leadingIcon="profile"
                  title={t('settings.customerInfo.title')}
                  description={t('settings.customerInfo.description')}
                  onClick={handleCustomerInfo}
                  showTrailingIcon={true}
                  data-testid="settings-customer-info-button"
                />
              </Container>
              <Container {...itemProps}>
                <ListItem
                  leadingIcon="location"
                  title={t('settings.addressBook.title')}
                  description={t('settings.addressBook.description')}
                  onClick={handleAddressBook}
                  showTrailingIcon={true}
                  data-testid="settings-address-book-button"
                />
              </Container>
              <Container {...itemProps}>
                <ListItem
                  leadingIcon="phone"
                  title={t('settings.communication.title')}
                  description={t('settings.communication.description')}
                  onClick={handleCommunication}
                  showTrailingIcon={true}
                  data-testid="settings-communication-button"
                />
              </Container>
              {role === ROLE.CUSTOMER && onLogout && (
                <Container {...itemProps}>
                  <button
                    onClick={onLogout}
                    className="mt-40 self-start flex h-44 pl-16 pr-20 justify-center items-center gap-[5px] rounded-md border border-border-button-secondary bg-button-secondary text-left font-medium text-base leading-base text-text-button-secondary cursor-pointer"
                    type="button"
                    data-testid="settings-logout-button"
                  >
                    <Icon
                      name="logout"
                      size="medium"
                      color="default"
                      className="h-20"
                    />
                    <span>{t('settings.logOut')}</span>
                  </button>
                </Container>
              )}
            </Container>
          </Suspense>
        )
      default:
        return null
    }
  }

  return (
    <BootSkeletonProvider hasBootSkeleton={skipSkeletonDelay}>
      <div
        className={cn(
          'bg-background min-h-full lg:flex lg:gap-20 lg:px-80 lg:pt-64 lg:pb-120',
          'max-lg:flex max-lg:flex-col max-lg:overflow-hidden',
          reserveFixedFooterInset
            ? 'max-lg:min-h-0 max-lg:flex-1'
            : 'max-lg:min-h-dvh max-lg:max-h-dvh',
          shellContentClassName,
        )}
      >
        {/* Header - Left sidebar on desktop; fixed on mobile */}
        <header className="flex shrink-0 flex-col max-lg:pt-40 lg:w-5/12 xl:w-1/3">
          {/* Profile Header */}
          <div className="flex flex-col gap-10 lg:gap-40">
            <div className="flex items-start gap-10 max-lg:px-16">
              <h1 className="text-text tracking-[-.015em] font-medium text-2xl leading-2xl lg:text-3xl lg:leading-3xl">
                {getDisplayName()}
              </h1>
              <EmployeeBadge role={role} email={customerData.email} />
              {customerStatus === 'serving' && (
                <span className="rounded-md bg-surface-brand px-8 py-4 text-xs font-medium text-text-inverse">
                  {t('status.serving')}
                </span>
              )}
              {customerStatus === 'waiting' && (
                <span className="rounded-md bg-surface-secondary px-8 py-4 text-xs font-medium text-text-secondary">
                  {t('status.waiting')}
                </span>
              )}
              {customerStatus === 'activeInCart' && (
                <span className="rounded-md bg-surface-success px-8 py-4 text-xs font-medium text-text-success">
                  {t('status.active')}
                </span>
              )}
            </div>

            {/* Navigation Tabs */}
            <nav className="flex flex-nowrap max-lg:gap-24 max-lg:px-16 max-lg:border-b max-lg:border-border lg:flex-col lg:items-start lg:gap-16 overflow-x-auto scrollbar-hide">
              {availableTabs.map((tab) => (
                <button
                  key={tab.id}
                  ref={(el) => {
                    tabButtonRefs.current[tab.id] = el
                  }}
                  onClick={() => handleTabClick(tab.id)}
                  disabled={tab.disabled}
                  className={`relative text-left font-medium text-base leading-base transition-colors whitespace-nowrap max-lg:py-12 ${effectiveActiveTab === tab.id ? 'text-text' : 'text-text-secondary'} ${tab.disabled ? 'disabled:text-text-disabled disabled:cursor-default' : 'cursor-pointer hover:text-button-hover'}`}
                  type="button"
                  aria-current={
                    effectiveActiveTab === tab.id ? 'page' : undefined
                  }
                  data-testid={`tab-button-${TAB_TEST_IDS[tab.id]}`}
                >
                  {t(`tabs.${tab.id}`)}
                  {effectiveActiveTab === tab.id &&
                    (disableAnimations ? (
                      <div className="absolute bottom-0 left-0 right-0 h-2 bg-border-secondary lg:hidden" />
                    ) : (
                      <Suspense fallback={null}>
                        <MotionDiv
                          layoutId="activeTabIndicator"
                          className="absolute bottom-0 left-0 right-0 h-2 bg-border-secondary lg:hidden"
                          transition={tabIndicatorTransition}
                        />
                      </Suspense>
                    ))}
                </button>
              ))}
            </nav>
          </div>
        </header>

        {/* Main Content Area - Right side on desktop */}
        <main
          className={cn(
            'relative lg:w-7/12 xl:w-2/3',
            'max-lg:min-h-0 max-lg:flex-1 max-lg:overflow-y-auto max-lg:overscroll-y-contain',
            reserveFixedFooterInset && [
              fixedFooterScrollPadding,
              '[scroll-padding-bottom:calc(5rem+env(safe-area-inset-bottom,0px))]',
            ],
          )}
        >
          <AnimatedContentWrapper
            animationKey={effectiveActiveTab}
            isPending={isPending}
          >
            {children ?? (
              <div className="flex flex-col">
                {renderDefaultTabContent()}
                {reserveFixedFooterInset && (
                  <div
                    aria-hidden
                    className={cn('shrink-0', fixedFooterInsetClass)}
                  />
                )}
              </div>
            )}
          </AnimatedContentWrapper>
        </main>
      </div>
    </BootSkeletonProvider>
  )
}

function CustomerHubCoreWithData({
  SizePassports,
  children,
  accountNumber,
  customerProfileEmail,
  env,
  locationId,
  locationCode,
  customerStatus,
  activeTab,
  onTabChange,
  context,
  role,
  tracking,
  authToken,
  onLogout,
  stateContainerClassName,
  disableAnimations,
  analyticsConfig,
  onCustomerDataLoaded,
}: CustomerHubCoreProps) {
  const { t } = useI18N()
  const queryClient = useQueryClient()

  // Fetch customer data
  const {
    data: customerData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<CustomerData>({
    queryKey: ['customer', accountNumber, customerProfileEmail, env],
    queryFn: () =>
      fetchCustomerData(
        {
          accountNumber,
          customerProfileEmail,
        },
        env,
        authToken,
      ),
    enabled: !!accountNumber || !!customerProfileEmail,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  })

  // Shell boot (name + tab nav) follows the same skeleton contract as the
  // sections. It is a first-load state only: the customer record is cached, so
  // switching tabs never replays it.
  const { phase: shellPhase, contentClassName: shellContentClassName } =
    useSkeletonPhase(isLoading)

  // The nav's flag-gated tabs are resolved during boot as well, so the
  // placeholder renders the same tabs the nav will. GrowthBook answers from its
  // already-initialised instance, well inside the customer fetch, and until it
  // does the skeleton shows the always-present tabs — it under-promises rather
  // than advertising a tab that never arrives.
  const [bootTabFlags, setBootTabFlags] = useState({
    isSizePassportTabEnabled: false,
    isWardrobeTabEnabled: false,
  })
  // `CH-active-cards` decides whether the Overview boot screen paints an Active
  // Orders placeholder — without it the boot screen would promise a section
  // that never arrives.
  const [isBootActiveCardsOn, setIsBootActiveCardsOn] = useState(false)
  const [isBootOverviewTabOn, setIsBootOverviewTabOn] = useState(false)
  // Latches once the boot placeholders were actually painted, so the sections
  // that replace them skip the anti-flash delay instead of blanking the column.
  const [hasBootSkeleton, setHasBootSkeleton] = useState(false)
  const bootTab =
    activeTab ??
    getTabFromScreenParam() ??
    getDefaultTab(role, isBootOverviewTabOn)

  useEffect(() => {
    if (shellPhase === 'skeleton') setHasBootSkeleton(true)
  }, [shellPhase])

  useEffect(() => {
    if (!isLoading) return
    let isMounted = true
    const bootEnv = normalizeEnvironment(env)
    const bootLocationId =
      locationId ??
      (typeof window !== 'undefined'
        ? (new URLSearchParams(window.location.search).get('locationId') ?? '')
        : '')

    void Promise.all([
      isSizePassportEnabled(bootEnv, bootLocationId, role).catch(() => false),
      isWardrobeEnabled(bootEnv, bootLocationId, role).catch(() => false),
      isActiveCardsEnabled(bootEnv, bootLocationId, role).catch(() => false),
      isOverviewTabEnabled(bootEnv, bootLocationId, role).catch(() => false),
    ]).then(
      ([
        isSizePassportTabEnabled,
        isWardrobeTabEnabled,
        isActiveCardsOn,
        isOverviewTabOn,
      ]) => {
        if (isMounted) {
          setBootTabFlags({
            isSizePassportTabEnabled:
              isSizePassportTabEnabled && Boolean(SizePassports),
            isWardrobeTabEnabled,
          })
          setIsBootActiveCardsOn(isActiveCardsOn)
          setIsBootOverviewTabOn(isOverviewTabOn)
        }
      },
    )

    return () => {
      isMounted = false
    }
  }, [SizePassports, env, isLoading, locationId, role])

  useEffect(() => {
    if (!customerData) return
    onCustomerDataLoaded?.(customerData)
  }, [customerData, onCustomerDataLoaded])

  useEffect(() => {
    if (!customerData) return
    const userType =
      role === ROLE.CUSTOMER
        ? [avo.UserType.CUSTOMER]
        : [avo.UserType.STYLE_ADVISOR]
    setProfileSystemProperties({
      locationId:
        locationId ??
        new URLSearchParams(window.location.search).get('locationId') ??
        '',
      userId: customerData.accountNumber,
      loggedIn: avo.LoggedIn.TRUE,
      pageType: avo.PageType.ACCOUNT,
      // Always a value: the generated setter skips undefined, which would
      // leave the previous profile's country in place.
      country: (
        normalizeCountryCode(customerData.countryCode) ??
        getResolvedCountryCode()
      ).toLowerCase() as CountryValueType,
      language: (document.documentElement.lang?.split('-')[0] ||
        'en') as LanguageValueType,
    })
    avo.customerHubOpened({
      eventLabel: null,
      eventCategory: null,
      eventLocation: 'customer_hub',
      employeeId: tracking?.employeeId ?? '',
      userType,
    })
  }, [customerData, locationId, role, tracking])

  // Mutation for updating customer data
  const updateCustomerMutation = useMutation({
    mutationFn: (payload: UpdateCustomerPayload) =>
      updateCustomer(
        customerData?.accountNumber ?? accountNumber,
        payload,
        env,
        authToken,
      ),
    onSuccess: (updatedData) => {
      // Update the query cache with the new data
      queryClient.setQueryData(
        ['customer', accountNumber, customerProfileEmail, env],
        updatedData,
      )
      debug.log('[CustomerAPI] Customer data updated successfully')
    },
    onError: (error) => {
      console.error('[CustomerAPI] Failed to update customer:', error)
    },
  })

  // Loading state — the shell (name + tab nav) renders as a skeleton under the
  // same anti-flash contract as the sections (RP-5936): layout is reserved from
  // the first frame, the shimmer only appears once the fetch passes 150ms, and
  // a cached response swaps straight to content with no flash.
  if (shellPhase !== 'content') {
    return (
      <CustomerHubProvider
        role={role}
        context={context}
        tracking={tracking}
        disableAnimations={disableAnimations}
      >
        <HubShellSkeleton
          tabIds={getRoleTabs(
            buildTabs(bootTabFlags),
            role,
            isBootOverviewTabOn,
          ).map((tab) => tab.id)}
          className={cn(
            shellPhase === 'reserve' && 'invisible',
            stateContainerClassName,
          )}
        >
          <BootContentSkeleton
            tab={bootTab}
            showActiveOrders={isBootActiveCardsOn}
            showTimeline={
              role === ROLE.STYLE_ADVISOR &&
              !isCustomerTimelineHidden(locationCode ?? '')
            }
          />
        </HubShellSkeleton>
      </CustomerHubProvider>
    )
  }

  // Error state
  if (isError) {
    return (
      <CustomerHubProvider
        role={role}
        context={context}
        tracking={tracking}
        disableAnimations={disableAnimations}
      >
        <div
          className={cn(
            'bg-background min-h-dvh max-lg:min-h-0 max-lg:flex-1 flex items-center justify-center p-24',
            stateContainerClassName,
          )}
        >
          <div className="flex flex-col items-center gap-16 max-w-md text-center">
            <Icon name="info-circle" size={48} color="error" />
            <div className="flex flex-col gap-8">
              <h2 className="text-xl leading-xl font-medium text-text">
                {t('errors.failedToLoadCustomer')}
              </h2>
              <p className="text-base text-text-secondary">
                {error instanceof Error
                  ? error.message
                  : t('errors.unexpectedError')}
              </p>
            </div>
            <Button
              onClick={() => refetch()}
              variant="filled"
              size="m"
              data-testid="button-try-again"
            >
              {t('actions.tryAgain')}
            </Button>
          </div>
        </div>
      </CustomerHubProvider>
    )
  }

  // No data state (shouldn't happen with proper error handling, but just in case)
  if (!customerData) {
    return (
      <CustomerHubProvider
        role={role}
        context={context}
        tracking={tracking}
        disableAnimations={disableAnimations}
      >
        <div
          className={cn(
            'bg-background min-h-dvh max-lg:min-h-0 max-lg:flex-1 flex items-center justify-center p-24',
            stateContainerClassName,
          )}
        >
          <p className="text-base text-text-secondary">
            {t('errors.noCustomerData')}
          </p>
        </div>
      </CustomerHubProvider>
    )
  }

  return (
    <CustomerHubProvider
      role={role}
      context={context}
      tracking={tracking}
      disableAnimations={disableAnimations}
    >
      <CustomerDataProvider
        customerData={customerData}
        updateMutation={updateCustomerMutation}
      >
        <ToastProvider>
          <ModalProvider>
            <CustomerHubContent
              SizePassports={SizePassports}
              customerData={customerData}
              env={env}
              locationId={locationId}
              locationCode={locationCode}
              customerStatus={customerStatus}
              activeTab={activeTab}
              onTabChange={onTabChange}
              role={role}
              tracking={tracking}
              authToken={authToken}
              onLogout={onLogout}
              analyticsConfig={analyticsConfig}
              shellContentClassName={shellContentClassName}
              bootFlags={{
                ...bootTabFlags,
                isActiveCardsOn: isBootActiveCardsOn,
                isOverviewTabOn: isBootOverviewTabOn,
              }}
              bootPaintedTab={
                hasBootSkeleton && bootSkeletonCoversTab(bootTab)
                  ? bootTab
                  : null
              }
            >
              {children}
            </CustomerHubContent>
          </ModalProvider>
        </ToastProvider>
      </CustomerDataProvider>
    </CustomerHubProvider>
  )
}

/**
 * Makes sure this package's analytics are wired before anything tracks.
 *
 * `App` already does this, but `@susu/active-customers-list` and the
 * storefront render `CustomerHubCore` directly, so without this a composed
 * mount never registers with @susu/analytics-client and its events never
 * reach the host's Segment instance.
 *
 * Both calls are idempotent, so the `App` path is unaffected: this effect runs
 * first (a child's `useLayoutEffect` precedes its parent's) and `App`'s call
 * becomes the no-op rather than the other way round.
 *
 * `useLayoutEffect` rather than `useEffect` for the same reason `App` uses it:
 * it completes before any child's `useEffect`, so the destination is in place
 * before the first `customer_hub_opened` fires.
 */
function useAnalyticsReady(
  env: CustomerHubCoreProps['env'],
  analyticsConfig: CustomerHubCoreProps['analyticsConfig'],
) {
  useLayoutEffect(() => {
    // Hosts that skip `App` never configured this either, which would leave the
    // dropped-event warning in `lib/analytics.ts` silent on tst/acc as well.
    configureDebugLogging(env)
    initializeAnalytics(normalizeEnvironment(env), analyticsConfig)
  }, [env, analyticsConfig])
}

export default function CustomerHubCore(props: CustomerHubCoreProps) {
  useAnalyticsReady(props.env, props.analyticsConfig)

  const language =
    props.language ||
    (typeof document !== 'undefined'
      ? document.documentElement.lang.split('-')[0]
      : '') ||
    'en'

  return (
    <ClientI18NProvider language={language} messages={translations}>
      <QueryClientProvider client={customerHubQueryClient}>
        <CustomerHubCoreWithData {...props} />
      </QueryClientProvider>
    </ClientI18NProvider>
  )
}
